import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  ClipboardCheck,
  Bell,
  Users,
  Building2,
  ShieldPlus,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/format";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "administrator";
  const hasPro = user && (isAdmin || user.role === "manager" || user.pro_access);

  const link = ({ isActive }) => "sidebar-link" + (isActive ? " active" : "");

  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0F172A] text-slate-100 z-30">
      <div className="px-6 pt-7 pb-6 border-b border-slate-800/70">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">Complio</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-400">Compliance Tracker</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1" data-testid="sidebar-nav">
        <NavLink to="/dashboard" className={link} data-testid="nav-dashboard">
          <LayoutDashboard className="w-4 h-4" />
          <span>Dashboard</span>
        </NavLink>
        <NavLink to="/compliances" className={link} data-testid="nav-compliances">
          <ClipboardList className="w-4 h-4" />
          <span>VAT and Accounting</span>
        </NavLink>
        <NavLink to="/excise" className={link} data-testid="nav-excise">
          <ClipboardList className="w-4 h-4" />
          <span>Excise</span>
        </NavLink>
        {hasPro && (
          <NavLink to="/pro-compliances" className={link} data-testid="nav-pro">
            <ShieldPlus className="w-4 h-4" />
            <span>PRO Compliance</span>
          </NavLink>
        )}
        <NavLink to="/audit-compliance" className={link} data-testid="nav-audit">
          <ClipboardCheck className="w-4 h-4" />
          <span>Audit and Other Compliance</span>
        </NavLink>
        <NavLink to="/corporate-tax" className={link} data-testid="nav-ct">
          <ClipboardCheck className="w-4 h-4" />
          <span>Corporate Tax</span>
        </NavLink>
        <NavLink to="/clients" className={link} data-testid="nav-clients">
          <Building2 className="w-4 h-4" />
          <span>Client Master</span>
        </NavLink>
        <NavLink to="/reminders" className={link} data-testid="nav-reminders">
          <Bell className="w-4 h-4" />
          <span>Reminders</span>
        </NavLink>
        {isAdmin && (
          <NavLink to="/users" className={link} data-testid="nav-users">
            <Users className="w-4 h-4" />
            <span>Users</span>
          </NavLink>
        )}
      </nav>

      <div className="px-3 pb-4">
        <div className="rounded-lg bg-slate-800/50 px-3 py-3 mb-2">
          <div className="text-xs uppercase tracking-widest text-slate-400">Signed in</div>
          <div className="mt-1 font-medium text-sm truncate" data-testid="sidebar-user-name">
            {user?.name || user?.email}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{roleLabel(user?.role)}</div>
        </div>
        <button
          onClick={logout}
          className="sidebar-link w-full text-left"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
